/**
 * La chorégraphie d'entrée du site, en un seul observateur.
 *
 * Le probleme qu'elle regle : les classes .animate-fade-in et .animate-slide-up
 * sont des animations CSS pures, qui demarrent au montage du composant. Sur une
 * page de 9 200 px, le pied de page terminait son fondu 0,6 s apres le
 * chargement, six mille pixels sous le pli. 13 des 14 entrees du site se
 * jouaient dans le vide. Une animation qu'on ne voit pas n'est pas une
 * animation, c'est un cout de rendu.
 *
 * Le systeme est pilote par attribut : n'importe quel element portant
 * data-reveal est pris en charge. Un SEUL IntersectionObserver les surveille
 * tous, au lieu d'un par composant.
 *
 * Filet de securite : l'etat cache n'est applique que sous
 * @media (scripting: enabled), dans index.css. Si le script ne tourne pas
 * (JS coupe, erreur, robot), le contenu est visible d'emblee. Une entree ratee
 * est un detail ; du contenu invisible est une page cassee.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export function Choregraphie() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const observateur = new IntersectionObserver(
      (entrees) => {
        for (const e of entrees) {
          if (!e.isIntersecting) continue;
          e.target.classList.add("est-visible");
          // Une entree ne se joue qu'une fois : rejouer a chaque passage
          // transforme le defilement en clignotement.
          observateur.unobserve(e.target);
        }
      },
      // Le declenchement se fait un peu avant le bord bas de la fenetre, pour
      // que l'element soit deja en mouvement quand il entre vraiment.
      { rootMargin: "0px 0px -12% 0px", threshold: 0.01 }
    );

    // Les conteneurs d'ambiance : leurs boucles ne tournent que dans le champ.
    // Marge large, pour qu'elles soient deja lancees quand on arrive dessus.
    const veilleuse = new IntersectionObserver(
      (entrees) => {
        for (const e of entrees) e.target.classList.toggle("en-vue", e.isIntersecting);
      },
      { rootMargin: "25% 0px" }
    );

    const recenser = () => {
      document
        .querySelectorAll<HTMLElement>("[data-reveal]:not(.est-visible)")
        .forEach((el) => observateur.observe(el));
      document
        .querySelectorAll<HTMLElement>("[data-anim]")
        .forEach((el) => veilleuse.observe(el));
    };

    // Un rAF laisse le routeur finir de peindre la nouvelle page.
    let t = requestAnimationFrame(recenser);

    // Le filtre par categorie du blog remonte une autre liste de cartes, et les
    // donnees arrivent apres le premier rendu. Sans ce guetteur, toute carte
    // apparue apres coup echapperait a l'observateur et resterait invisible
    // POUR TOUJOURS, puisque son etat cache n'est jamais leve.
    const guetteur = new MutationObserver(() => {
      cancelAnimationFrame(t);
      t = requestAnimationFrame(recenser);
    });
    guetteur.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(t);
      guetteur.disconnect();
      veilleuse.disconnect();
      observateur.disconnect();
    };
  }, [pathname]);

  return null;
}

/**
 * Le decalage d'une carte dans une serie. A poser en style inline.
 *
 * 90 ms par rang, plafonne a cinq : assez pour lire une cascade, et sur une
 * grille de trente articles la derniere carte n'attend pas trois secondes.
 */
export const decalage = (i: number) =>
  ({ "--rev-delai": `${Math.min(i, 5) * 90}ms` }) as React.CSSProperties;
